<template lang="pug">
.thesis-item
  .thesis-item-header
    .thesis-kicker
      v-icon.thesis-kicker-icon(size='17') mdi-school-outline
      span {{ t('thesisStatus.for') }}
      span(v-for='(relevance, i) in thesis.relevance', :key='i')
        | {{ relevance.for }}{{ i < thesis.relevance.length - 1 ? ", " : "" }}
    h3.thesis-title {{ title }}
  details.thesis-description-disclosure(v-if='description', :open='descriptionOpenByDefault')
    summary.thesis-description-summary {{ t('thesisStatus.description') }}
    .thesis-description-panel
      p.thesis-description {{ description }}
  dl.thesis-meta
    .thesis-meta-row(v-if='renderSupervisor')
      dt {{ t('thesisStatus.supervisor') }}
      dd {{ formatFullName(thesis.supervisorPerson) }}
    .thesis-meta-row(v-if='renderTutor')
      dt {{ t('thesisStatus.tutor') }}
      dd
        span {{ formatFullName(thesis.tutorPerson) }}
        a.thesis-mail(:href='`mailto:${thesis.tutorPerson.email}`') {{ thesis.tutorPerson.email }}
    .thesis-meta-row(v-if='renderStudent')
      dt {{ t('thesisStatus.student') }}
      dd {{ thesis.studentName }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { THESIS_STATUSES, type ThesisLanguage } from '~/constants/domain'
import type { PersonContact } from '~/interfaces/person'
import type { ILanguage } from '~/interfaces/thesis'
import type { Thesis } from '~/models/thesis'
import { formatFullName } from '~/utils/person'

defineOptions({
  name: 'ThesisItemContent',
})

const props = defineProps({
  thesis: {
    type: Object as () => Thesis,
    required: true,
  },
  showSupervisor: {
    type: Boolean,
    required: true,
  },
  showTutor: {
    type: Boolean,
    required: true,
  },
  showStudent: {
    type: Boolean,
    required: true,
  },
  descriptionOpenByDefault: {
    type: Boolean,
    default: true,
  },
  personContext: {
    type: Object as () => PersonContact,
    required: true,
  },
})

const { locale, t } = useI18n()
const language = computed<ThesisLanguage>(() =>
  locale.value === 'de' ? 'de' : 'en',
)

const renderSupervisor = computed(() => {
  const supervisorExists = !!props.thesis.supervisorPerson
  return props.showSupervisor && supervisorExists
})

const renderTutor = computed(() => {
  if (!props.thesis.tutorPerson) return false
  return props.showTutor
})

const renderStudent = computed(() => {
  const studentNameExists = !!props.thesis.studentName
  const thesisAssignedOrfinished =
    props.thesis.status === THESIS_STATUSES[2] ||
    props.thesis.status === THESIS_STATUSES[3]
  return props.showStudent && studentNameExists && thesisAssignedOrfinished
})

const selectedLanguageEntry = computed<ILanguage | null>(() => {
  const fallbackEntry = props.thesis.languageSpecific[0] ?? null

  return (
    props.thesis.languageSpecific.find(
      (entry) => entry.language === language.value,
    ) ?? fallbackEntry
  )
})

const title = computed(() => {
  return selectedLanguageEntry.value?.title ?? ''
})

const description = computed(() => {
  return selectedLanguageEntry.value?.description ?? ''
})
</script>

<style scoped>
.thesis-item {
  display: grid;
  gap: 1rem;
  padding: 1.15rem 1.2rem;
  background: var(--surface-soft);
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--brand-primary);
}

.thesis-item-header {
  display: grid;
  gap: 0.65rem;
  min-width: 0;
}

.thesis-kicker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  color: var(--color-accent-soft);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.thesis-kicker-icon {
  color: var(--brand-primary);
}

.thesis-title {
  margin: 0;
  color: var(--color-text-strong);
  font-size: clamp(1.08rem, 1.4vw, 1.25rem);
  line-height: 1.3;
  font-family: var(--font-sans);
}

.thesis-description-disclosure {
  display: grid;
  gap: 0.7rem;
}

.thesis-description-summary {
  width: fit-content;
  color: var(--color-accent-soft);
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 700;
  list-style: none;
  user-select: none;
}

.thesis-description-summary::-webkit-details-marker {
  display: none;
}

.thesis-description-summary::before {
  content: '';
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  margin-right: 0.55rem;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg) translateY(-1px);
  transition: transform 0.2s ease;
}

.thesis-description-disclosure[open] .thesis-description-summary::before {
  transform: rotate(45deg) translateY(-3px);
}

.thesis-description-panel {
  padding: 0.9rem 1rem;
  background: var(--surface-card);
  border-left: 2px solid var(--border-default);
}

.thesis-description {
  margin: 0;
  color: var(--color-text);
  font-size: 0.98rem;
  line-height: 1.75;
  font-family: var(--font-sans);
  text-align: left;
  hyphens: auto;
}

.thesis-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem 1rem;
  margin: 0;
  padding-top: 0.15rem;
}

.thesis-meta-row {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
  padding-top: 0.8rem;
  border-top: 1px solid var(--border-subtle);
}

.thesis-meta-row dt {
  color: var(--color-text-muted);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.thesis-meta-row dd {
  margin: 0;
  color: var(--color-text-strong);
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.thesis-mail {
  display: inline-block;
  margin-left: 0.55rem;
  color: var(--brand-primary);
  text-decoration: none;
  font-weight: 700;
}

.thesis-mail:hover {
  text-decoration: underline;
}

@media (max-width: 760px) {
  .thesis-item {
    padding: 1rem;
  }

  .thesis-meta {
    grid-template-columns: 1fr;
  }

  .thesis-meta-row {
    grid-template-columns: 1fr;
    gap: 0.2rem;
  }

  .thesis-mail {
    display: block;
    margin-left: 0;
    margin-top: 0.2rem;
  }
}
</style>
